import * as React from 'react';

import gql from 'graphql-tag';
import { toNumber } from 'lodash';
import { graphql } from 'react-apollo';
import { InjectedRouter } from 'react-router';

// @ts-ignore
import OrganizationActions from 'actions/OrganizationActions';
// @ts-ignore
import PromptActions from 'actions/PromptActions';
import ToastActions from 'actions/ToastActions';
import { withRouter } from 'app/context/Router';
import CurrentUser from 'auth/CurrentUser';
import BoxBody from 'components/BoxBody';
import BoxFooter from 'components/BoxFooter';
import Button from 'components/ButtonDeprecated';
import DialogContent from 'components/DialogContent';
import Field from 'components/Field';
// @ts-ignore
import FormCheckBox from 'components/FormCheckBox';
import FormDeprecated from 'components/FormDeprecated';
// @ts-ignore
import FormInput from 'components/FormInput';
import Image from 'components/Image';
import Label from 'components/Label';
import Link from 'components/Link';
import Row from 'components/Row';
// @ts-ignore
import SchoolPicker from 'components/SchoolPicker';
import Text from 'components/Text';
// @ts-ignore
import { PromptNames } from 'constants/PromptConstants';
import Group from 'entities/Group';
// @ts-ignore
import Organization from 'entities/Organization';
// @ts-ignore
import GroupActions from 'groups/actions/GroupActions';
// @ts-ignore
import FeatureStore from 'stores/FeatureStore';
// @ts-ignore
import PromptStore from 'stores/PromptStore';
// @ts-ignore
import UserActions from 'user/UserActions';

import { FocusEventHandler, InputChangeHandler } from 'types/typescript/eventHandlers';

import USER_ORGANIZATION_QUERY from 'organizations/queries/UserOrganizationsQuery.gql';

import './styles/SetupGroup.scss';

const FACEBOOK_TRACKING_PIXEL =
  'https://www.facebook.com/offsite_event.php?id=6008327779772&value=0.01&currency=USD';

type Props = {
  router: InjectedRouter;

  // gql mutation
  updateUserOrgs: (orgIds: number[] | number) => Promise<void>;
};

type State = {
  schoolId?: number | null;
  formIsValid: boolean;
  u13Group: boolean;
  submitting: boolean;
  warning?: any;
};

class SetupGroup extends React.Component<Props, State> {
  state: State = {
    schoolId: null,
    submitting: false,
    u13Group: false,
    formIsValid: false,
  };

  handleSubmit = (data: any) => {
    this.setState({ submitting: true });

    const { groupName, overThirteen } = data;
    const { schoolId } = this.state;

    const groupToCreate = new Group();
    groupToCreate.setName(groupName);
    groupToCreate.setHasChildren(!overThirteen);
    groupToCreate.setCanSubscribersInititateChats(
      FeatureStore.hasFeature('subscriber_initiated_chats_default')
    );

    const onError = (error: Error) => {
      this.setState({ submitting: false });
      ToastActions.showAPIError(error);
    };

    const createGroup = () => {
      GroupActions.create(groupToCreate).then((group: Group) => {
        // Controls visibility of group invite modal when landing in app
        GroupActions.setGroupCreatedDuringSetup(group);

        if (!PromptStore.hasDismissed(PromptNames.NUX_SETUP_CLASS)) {
          PromptActions.recordDismissal(PromptNames.NUX_SETUP_CLASS)
            .then(() => {
              UserActions.loadCurrentUser();
            })
            .catch((error: Error) =>
              console.error('SetupGroup - can’t record NUX_SETUP_CLASS dismissal', error)
            );
        }
        this.props.router.push('/classes/?origin=created_group');
      }, onError);
    };

    if (schoolId) {
      this.props.updateUserOrgs(schoolId).then(() => {
        UserActions.loadCurrentUser();
        groupToCreate.setOrganization(new Organization({ id: schoolId }));
        OrganizationActions.loadUserOrganizations();
        createGroup();
      }, onError);
      return;
    }

    createGroup();
  };

  handleInputBlur = (event: React.FocusEvent<any>) => {
    // Hack to redirect before firing validation if the thing that was clicked on the
    // cause the blur was a link to somewhere else

    if (
      event.relatedTarget &&
      // @ts-ignore prettyer sure we have href on these but just in case...
      event.relatedTarget.href &&
      // @ts-ignore
      event.relatedTarget.href.endsWith('/setup/subscriptions')
    ) {
      this.props.router.push('/setup/subscriptions');
    }
  };

  validateClassName = (e: React.FocusEvent<any>) => {
    const { value } = e.target;
    let warning = null;
    if (value === '81010' || value.startsWith('@') || value.startsWith('rmd.me')) {
      warning = (
        <div>
          {'It looks like you’re trying to '}
          <Link
            className="SetupGroup-warning"
            to="/setup/subscriptions"
            eventElementName="SetupGroup-warning">
            join a class
          </Link>
          .
        </div>
      );
    }
    this.setState({
      warning,
    });
  };

  renderFBTrackingPixel = () => {
    // Don't render the tracking pixel for subsequent logins
    if (CurrentUser.getUser().getSignInCount() > 1) {
      return null;
    }
    return (
      <Image className="SetupGroup-fbPixel" src={FACEBOOK_TRACKING_PIXEL} width="1" height="1" />
    );
  };

  renderUnder13Checkbox = () => {
    return (
      <FormCheckBox
        name="overThirteen"
        defaultChecked
        onChange={() => this.setState({ u13Group: !this.state.u13Group })}
        value={!this.state.u13Group}>
        <div>I will only message people 13 and older</div>
        <div className="SetupGroup-labelSubtext">
          It’s okay if students are under 13. We’ll ask for a parent’s email address to keep
          everyone in the loop.
        </div>
      </FormCheckBox>
    );
  };

  renderSchoolPicker = () => {
    return (
      <Field className="SetupGroup-field">
        <Label>School</Label>
        <Row size="xsmall">
          <SchoolPicker
            schoolId={this.state.schoolId || undefined}
            placeholder="Search for a school"
            onSelect={(itemKey: React.ReactText, title: string) => {
              this.setState({ schoolId: toNumber(itemKey) });
            }}
            onClear={() => this.setState({ schoolId: null })}
            onCreateOrganization={org => this.setState({ schoolId: org.id })}
          />
        </Row>
        <Text color="graphite" size="xxsmall">
          Classes with students under 13 must be linked to a school
        </Text>
      </Field>
    );
  };

  render() {
    const props = {
      title: 'Create a class',
      subtext: `Once you create a class, you can invite students and parents,
         send announcements, and start conversations.`,
      size: 'small',
    } as const;
    return (
      <DialogContent {...props} className="SetupGroup">
        <BoxBody divided>
          <FormDeprecated
            onValidSubmit={this.handleSubmit}
            onValid={() =>
              this.setState({
                formIsValid: true,
              })
            }
            onInvalid={() =>
              this.setState({
                formIsValid: false,
              })
            }>
            <FormInput
              label="Class name"
              onBlur={this.handleInputBlur}
              onChange={this.validateClassName}
              required
              className="SetupGroup-GroupName"
              type="text"
              name="groupName"
              placeholder="e.g. Math 101, Soccer Team, Chess Club"
              autofocus
              showErrorAfterBlur
              warning={this.state.warning}
              validations={{
                minLength: 3,
                maxLength: 60,
              }}
              validationError="The class name must be between 3 and 60 characters long."
            />
            {CurrentUser.getUser().isTeacher() ? (
              this.renderUnder13Checkbox()
            ) : (
              <div>
                <FormInput type="hidden" name="overThirteen" value />
                <p className="SetupGroup-overThirteenText">
                  This class can only include people who are 13 or older.
                </p>
              </div>
            )}
            {this.state.u13Group && this.renderSchoolPicker()}

            <Button
              className="SetupGroup-button-submit"
              disabled={
                this.state.submitting ||
                !this.state.formIsValid ||
                (this.state.u13Group && !this.state.schoolId)
              }
              type="submit"
              level="primary"
              eventElementName="SetupGroup-button-submit">
              Create
            </Button>
          </FormDeprecated>
        </BoxBody>
        <BoxFooter>
          {'Looking for a class? '}
          <Link
            className="SetupGroup-JoinClass"
            onClick={() => this.props.router.push('/setup/subscriptions')}
            eventElementName="SetupGroup-JoinClass">
            Join a class
          </Link>
        </BoxFooter>
        {this.renderFBTrackingPixel()}
      </DialogContent>
    );
  }
}

const withMutations = graphql(
  gql`
    mutation updateUserOrg($orgId: Int!) {
      updateUserOrgs(input: { organization_ids: [$orgId] }) {
        error {
          message
        }
      }
    }
  `,
  {
    props({ mutate }) {
      return {
        updateUserOrgs(orgId: number) {
          return (
            mutate &&
            // @ts-ignore new ts upgrades to apollo libs surfaced type mismatches.
            mutate({
              variables: { orgId },
              refetchQueries: [
                {
                  query: USER_ORGANIZATION_QUERY,
                },
              ],
            })
          );
        },
      };
    },
  }
);

export { SetupGroup };
// @ts-ignore this needs to be converted to render function but not today.
export default withRouter(withMutations(SetupGroup));
